import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Button, Checkbox, Pagination, App, Empty, Modal, Dropdown, Menu, BackTop } from 'antd';
import { DownOutlined, MenuOutlined, SearchOutlined, PlusOutlined, ExportOutlined, DeleteOutlined, EditOutlined, CloseOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearYourTownloopSearchValue } from '@/store/slices/searchBarSlice';
import { eventBus } from '@/utils/eventBus';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getServiceProduct, deleteService, getServiceProductDetail, getServiceProductPageListByKeyword } from '@/api/Services';
import styles from './YourProducts.module.scss';
import { useTranslation } from 'react-i18next';

// 标签项
const tabItems = [
  { label: 'All', key: 'all' },
  { label: 'For Sale', key: 'forSale' },
  { label: 'Stocked', key: 'stocked' },
  { label: 'Draft', key: 'draft' },
  { label: 'Trash', key: 'trash' },
  { label: 'Violation', key: 'violation' },
  { label: 'Obsolete Delisting', key: 'obsolete' },
];

// 产品类型定义 - 更新为新的接口结构
interface Product { 
  service_product_value: {
    service_id: number;
    product_category: {
      product_category_id: number;
      product_category_name: string;
      product_category_type_id: number;
      product_category_type_name: string;
      parent_id: number;
      is_active: boolean;
      is_delete: boolean;
      order_by: number | null;
    };
    service_name: string;
    service_description: string;
    create_time: string;
    update_time: string;
    user: {
      user_id: string;
      merchant_id: string;
      first_name: string;
      last_name: string;
      nick_name: string;
      username: string;
      email: string;
      phone: string;
      photo: string | null;
      gender: number;
      birthday: string | null;
      level: number;
      is_delete: boolean;
      create_time: string;
      update_time: string;
    };
  };
  product_main_images: Array<{
    id: number;
    service_product: any;
    media_type: number;
    file: {
      id: number;
      original_name: string;
      encrypted_name: string;
      host: string;
      file: string;
      size: number;
      content_type: string;
      upload_time: string;
    };
    media_file_url: string;
    create_time: string;
  }>;
  sales_specifications: Array<{
    name: string;
    price: string;
    duration: number;
    is_on_shelf: boolean;
  }>;
}

// 分类类型定义
interface Category {
  id: string;
  name: string;
  icon?: string;
  description?: string;
}

// 服务产品详情类型定义
interface ServiceProductDetail {
  service_id: number;
  product_category: {
    product_category_id: number;
    product_category_name: string;
    product_category_type_id: number;
    product_category_type_name: string;
    parent_id: number;
    is_active: boolean;
    is_delete: boolean;
    order_by: number | null;
  };
  service_name: string;
  service_description: string;
  create_time: string;
  update_time: string;
  user: {
    user_id: string;
    merchant_id: string;
    first_name: string;
    last_name: string;
    nick_name: string;
    username: string;
    email: string;
    phone: string;
    photo: string | null;
    gender: number;
    birthday: string | null;
    level: number;
    is_delete: boolean;
    create_time: string;
    update_time: string;
  };
}

const ServiceCategory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 标签状态
  const [activeTab, setActiveTab] = useState('all');

  // 搜索关键字
  const [searchValue, setSearchValue] = useState<string>('');

  // 选中的产品
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  // 页面尺寸
  const isMobile = useAppSelector((state) => state.searchBar.isMobile);


  // 发布产品弹窗
  // const [publishModalVisible, setPublishModalVisible] = useState<boolean>(false);
  // const [selectedCategory, setSelectedCategory] = useState<string>('');

  // yourTownloopSearchValue关键字
  const yourTownloopSearchValue = useAppSelector((state) => state.searchBar.yourTownloopSearchValue);

  // 产品详情数据
  const [productDetail, setProductDetail] = useState<ServiceProductDetail | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // 模拟产品分类数据
  // const productCategories: Category[] = [
  //   {
  //     id: '1',
  //     name: 'Product Category',
  //     description: 'Physical products that can be shipped'
  //   },
  //   {
  //     id: '2',
  //     name: 'Service Category',
  //     description: 'Services that can be provided online or offline'
  //   }
  // ];


  const [products, setProducts] = useState<Product[]>([]);

  // 分页信息
  const [goToPage, setGoToPage] = useState<string>('');

  // 分页参数类型定义
  interface PaginationParams {
    pageNum: number;
    pageSize: number;
    total: number;
    totalPages: number;
  }
  interface ServiceSearchParams {
    keyword?: string;
    pageNum: number;
    pageSize: number;
  }

  // 分页参数
  const [params, setParams] = useState<PaginationParams>({
    pageNum: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1
  });
  const fetchServiceList = async (pageNum: number, pageSize: number, keyword?: string, updateParams: boolean = true) => {
    try {
      // 使用搜索接口进行模糊查询
      const searchParams: ServiceSearchParams = {
        pageNum: pageNum,
        pageSize: pageSize,
        ...(keyword && keyword.trim() ? { keyword: keyword.trim() } : {})
      };
      
      const res = await getServiceProductPageListByKeyword(searchParams);
      
      console.log('fetchServiceList res', res);
      
      if (res.message === "Success") {
        setProducts(res.data || []);
        
        if (updateParams) {
          setParams(prevParams => ({
            ...prevParams,
            pageNum: pageNum,
            pageSize: pageSize,
            total: res.total || 0,
            totalPages: res.totalPages || 1,
          }));
        }
        
        return res;
      } 
    } catch (error) {
      console.error('获取服务列表失败:', error);
      message.error('获取服务列表失败');
    }
  };

  //获取服务分页列表
  const getProductListData = () => {
    // 使用当前分页参数和搜索关键字获取服务列表
    fetchServiceList(params.pageNum, params.pageSize, searchValue);
  }

  // 获取产品详情
  const fetchProductDetail = async (serviceId: string) => {
    try {
      // 根据接口要求传递service_product_id参数
      const res = await getServiceProductDetail({ service_product_id: serviceId });
      if (res.message === "Success") {
        setProductDetail(res.data);
        setIsEditing(true);
        console.log('产品详情数据:', res.data);
      } else {
        message.error('获取产品详情失败');
      }
    } catch (error) {
      console.error('获取产品详情失败:', error);
      message.error('获取产品详情失败');
    }
  };

  // 页面初始化
  useEffect(() => {
    getProductListData();
  }, []);

  // 监听URL参数，如果有ID则获取产品详情
  useEffect(() => {
    const serviceId = searchParams.get('id');
    if (serviceId) {
      fetchProductDetail(serviceId);
    }
  }, [searchParams]);

  // 监听params变化，重新获取数据
  useEffect(() => {
    if (params.pageNum > 0 && params.pageNum !== 1) {
      getProductListData();
    }
  }, [params.pageNum, params.pageSize]);

  // 监听当前页码变化，更新go to page输入框
  useEffect(() => {
    setGoToPage(params.pageNum.toString());
  }, [params.pageNum]);

  // 切换标签
  const changeTab = (key: string) => {
    console.log('key', key);
    setActiveTab(key);
    setSelectedProducts([]);
    setParams({
      ...params,
      pageNum: 1,
    });
  };

  // 搜索操作
  const searchAction = () => {
    console.log('searchValue', searchValue);
    // 搜索时重置到第一页
    setParams(prevParams => ({
      ...prevParams,
      pageNum: 1,
    }));
    fetchServiceList(1, params.pageSize, searchValue);
  };

  // 搜索处理键盘回车事件
  const searchHandleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchAction();
    }
  };

  // 重置搜索
  const resetSearch = () => {
    setSearchValue('');
    // 重置搜索时也重置到第一页
    setParams(prevParams => ({
      ...prevParams,
      pageNum: 1,
    }));
    fetchServiceList(1, params.pageSize, '');
  };

  // 全选/取消全选
  const handleSelectAll = (e: any) => {
    if (e.target.checked) {
      setSelectedProducts(products.map(product => product.service_product_value.service_id.toString()));
    } else {
      setSelectedProducts([]);
    }
  };

  // 选择单个产品
  const handleSelectProduct = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

    // 批量删除
  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) {
      message.warning('Please select services to delete');
      return;
    }
    Modal.confirm({
      title: 'Confirm Bulk Deletion',
      content: `Are you sure you want to delete the selected ${selectedProducts.length} services?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        deleteService(selectedProducts)
          .then((res) => {
            if (res.message === "Success") {
              message.success(`Successfully deleted ${selectedProducts.length} services`);
              setSelectedProducts([]);
              getProductListData(); // 重新获取服务列表
            } else {
            }
          })
          .catch((error) => {
          });
      },
    });
  };

  // 发布产品
  const handlePublishProduct = () => {
    // setPublishModalVisible(true);
    // setSelectedCategory('');
    navigate('/serviceCategory');
  };

  // 选择产品分类
  // const handleSelectCategory = (categoryId: string) => {
  //   setSelectedCategory(categoryId);
  //   // message.success(`选择了分类ID: ${categoryId}`);
  //   if (categoryId == '1') {
  //     navigate('/StorePublishProduct');
  //   } else {
  //     navigate('/serviceCategory');
  //   }
  //   // 这里可以根据选择的分类跳转到不同的产品发布页面

  // };

  // // 关闭发布产品弹窗
  // const handleClosePublishModal = () => {
  //   setPublishModalVisible(false);
  // };


  // 导出所有数据
  const handleExport = async () => {
    if (params.total === 0) {
      message.warning('No data available for export');
      return;
    }
    try {
      const allProducts: Product[] = [];
      const totalPages = Math.ceil(params.total / params.pageSize);
      // 分页获取所有数据
      for (let page = 1; page <= totalPages; page++) {
        const res = await fetchServiceList(page, params.pageSize, searchValue, false);
        
        if (res && res.data) {
          allProducts.push(...res.data);
        }
      }
      
      message.destroy();
      
      if (allProducts.length > 0) {
        exportToCSV(allProducts, `services_all_${new Date().toISOString().split('T')[0]}.csv`);
      } else {
      }
    } catch (error) {
      message.destroy();

    }
  };

  // 通用CSV导出函数
  const exportToCSV = (data: Product[], filename: string) => {
    // 准备CSV数据
    const csvData = data.map(product => ({
      'Service ID': product.service_product_value.service_id,
      'Service Name': product.service_product_value.service_name,
      'Category': product.service_product_value.product_category.product_category_name,
      'Price': product.sales_specifications && product.sales_specifications.length > 0
        ? `$${Math.min(...product.sales_specifications.map(spec => parseFloat(spec.price))).toFixed(2)}`
        : '$0.00',
      'Duration': product.sales_specifications && product.sales_specifications.length > 0
        ? `${product.sales_specifications[0]?.duration || 0} min`
        : '0 min',
      'Create Time': new Date(product.service_product_value.create_time).toLocaleString(),
      'Update Time': new Date(product.service_product_value.update_time).toLocaleString(),
    }));

    // 转换为CSV格式
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = (row as any)[header];
          // 如果值包含逗号、引号或换行符，需要用引号包围
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // 创建并下载文件
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };



  // 编辑产品
  const handleEdit = (id: string) => {
    // 优先使用service_id，如果没有则使用product_id
    navigate(`/serviceCategory?id=${id}`);
  };

  // 删除产品
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Confirm Deletion',
      content: 'Are you sure you want to delete this service?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        deleteService({ service_id_list: [id] }).then((res) => {
          if (res.message === "Success") {
            message.success('Service deleted successfully');
            setSelectedProducts(selectedProducts.filter(item => item !== id));
            getProductListData(); // 重新获取服务列表
          } else {
            message.error('Failed to delete service');
          }
        }).catch((error) => {
          console.error('Failed to delete service:', error);
          message.error('Failed to delete service');
        });
      },
    });
  };

  // 分页变化
  const paginationChange = (page: number, pageSize: number) => {
    console.log('page', page, pageSize);
    // 分页变化时保持搜索关键字
    fetchServiceList(page, pageSize, searchValue);
  };

  // 跳转到指定页
  const handleGoToPage = () => {
    const page = parseInt(goToPage);
    if (isNaN(page) || page < 1) {
      message.error('Please enter a valid page number');
      return;
    }

    // 检查页码是否超出范围
    if (page > params.totalPages) {
      message.error(`Maximum page number is ${params.totalPages}`);
      return;
    }

    fetchServiceList(page, params.pageSize, searchValue).then(() => {
      setGoToPage('');
    });
  };

  // 处理页码输入框回车事件
  const handlePageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  // mounted 和unmounted
  useEffect(() => {
    return () => {
      console.log('组件销毁时');
      // 销毁清除townloopSearchValue的值
      eventBus.emit('deleteTownloopSearchValue');
      dispatch(clearYourTownloopSearchValue());
    }
  }, [dispatch]);

  // yourTownloopSearchValue关键字的监听
  useEffect(() => {
    console.log('YourProducts组件yourTownloopSearchValue', yourTownloopSearchValue);
    if (yourTownloopSearchValue) {
      setSearchValue(yourTownloopSearchValue);
      searchAction();
    }
  }, [yourTownloopSearchValue]);

  // 渲染产品列表
  const renderProductList = () => {
    if (products.length === 0) {
      return (
        <Empty
          description={t('common.NoData')}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: '40px 0' }}
        />
      );
    }

    return (
      <div className={styles['products-table']}>
        <div className={styles['table-header']}>
          <div className={styles['checkbox-column']}>
            <Checkbox
              checked={selectedProducts.length === products.length && products.length > 0}
              indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length}
              onChange={handleSelectAll}
            />
          </div>
          <div className={styles['product-column']}>{t('yourProducts.ServiceName')}</div>
          <div className={styles['price-column']}>Minimum Price($)</div>
          <div className={styles['duration-column']}>Estimated Duration(min)</div>
          <div className={styles['time-column']}>Creation Time</div>
          <div className={styles['operations-column']}>Operations</div>
        </div>

        {products.map(product => (
          <div className={styles['table-row']} key={product.service_product_value.service_id}>
            <div className={styles['checkbox-column']}>
              <Checkbox
                checked={selectedProducts.includes(product.service_product_value.service_id.toString())}
                onChange={() => handleSelectProduct(product.service_product_value.service_id.toString())}
              />
            </div>
            <div className={styles['product-column']}>
              <div className={styles['product-info']}>
                <img
                  className={styles['product-image']}
                  src={product.product_main_images && product.product_main_images.length > 0
                    ? product.product_main_images[0]?.media_file_url
                    : 'https://via.placeholder.com/60x60?text=No+Image'
                  }
                  alt={product.service_product_value.service_name}
                  onClick={() => navigate(`/ServiceProductDetail/${product.service_product_value.service_id}`)}
                />
                <div className={styles['product-details']}>
                  <div className={styles['product-name']} title={product.service_product_value.service_name}>
                    {product.service_product_value.service_name}
                  </div>
                  <div className={styles['product-classification']}>
                    {product.service_product_value.product_category.product_category_name}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles['price-column']}>
              {product.sales_specifications && product.sales_specifications.length > 0
                ? `$${Math.min(...product.sales_specifications.map(spec => parseFloat(spec.price))).toFixed(2)}`
                : '$0.00'
              }
            </div>
            <div className={styles['duration-column']}>
              {product.sales_specifications && product.sales_specifications.length > 0
                ? `${product.sales_specifications[0]?.duration || 0} min`
                : '0 min'
              }
            </div>
            <div className={styles['time-column']}>
              {new Date(product.service_product_value.create_time).toLocaleString()}
            </div>
            <div className={styles['operations-column']}>
              <Button
                className={styles['edit-button']}
                onClick={() => handleEdit(product.service_product_value.service_id.toString())}
                icon={isMobile ? <EditOutlined /> : null}
              >
                {!isMobile && 'Edit'}
              </Button>
              <Button
                className={styles['delete-button']}
                onClick={() => handleDelete(product.service_product_value.service_id.toString())}
                icon={isMobile ? <DeleteOutlined /> : null}
              >
                {!isMobile && 'Delete'}
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 渲染发布产品弹窗
  // const renderPublishModal = () => {
  //   return (
  //     <Modal
  //       open={publishModalVisible}
  //       footer={null}
  //       closable={false}
  //       centered
  //       width={600}
  //       className={styles['publish-modal']}
  //       maskClosable={false}
  //     >
  //       <div className={styles['modal-content']}>
  //         <div className={styles['modal-header']}>
  //           <div className={styles['modal-close']} onClick={handleClosePublishModal}>
  //             <CloseOutlined />
  //           </div>
  //         </div>
  //         <div className={styles['category-container']}>
  //           {productCategories.map((category) => (
  //             <div
  //               key={category.id}
  //               className={`${styles['category-card']} ${selectedCategory === category.id ? styles['selected'] : ''}`}
  //               onClick={() => handleSelectCategory(category.id)}
  //             >
  //               <div className={styles['category-icon']}>
  //                 {category.id === '1' ? (
  //                   <div className={styles['product-icon']}></div>
  //                 ) : (
  //                   <div className={styles['service-icon']}></div>
  //                 )}
  //               </div>
  //               <div className={styles['category-name']}>{category.name}</div>
  //             </div>
  //           ))}
  //         </div>
  //       </div>
  //     </Modal>
  //   );
  // };

  return (
    <div className={styles['your-products-box']}>
      {/* <div className={styles['tabs-container']}>
        <Tabs
          className={styles['products-tabs']}
          items={tabItems}
          activeKey={activeTab}
          onChange={changeTab}
        />
      </div> */}

      <div className={styles['search-container']}>
        <div className={styles['search-input-wrapper']}>
          <input
            className={styles['search-input']}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={searchHandleKeyDown}
            type="text"
            placeholder={t('storeInformation.SearchService')}
          />
          <Button
            className={styles['search-button']}
            onClick={searchAction}
            icon={isMobile ? <SearchOutlined /> : null}
          >
            {isMobile ? '' : t('common.Search')}
          </Button>
          <Button
            className={styles['reset-button']}
            onClick={resetSearch}
          >
          {t('common.Reset')}
          </Button>
        </div>
      </div>

      <div className={styles['action-buttons']}>
        <Button
          className={styles['publish-button']}
          onClick={handlePublishProduct}
          icon={<PlusOutlined />}
        >
          {t('yourProducts.PublishService')}
        </Button>
        <Button
          className={styles['export-button']}
          onClick={handleExport}
          icon={<ExportOutlined />}
        >
          {t('yourProducts.ExportAll')}
        </Button>
        <Button
          className={styles['delete-button']}
          onClick={handleBulkDelete}
          icon={<DeleteOutlined />}
          disabled={selectedProducts.length === 0}
        >
          {t('yourProducts.BulkDelete')}
        </Button>
        <div className={styles['total-count']}>{t('searchList.Total')} {params.total} {t('storeInformation.Services')}  </div>
      </div>

      {renderProductList()}

      <div className={styles['pagination-container']}>
        <div className={styles['pagination-info']}>Total {params.total} Services</div>
        <Pagination
          onChange={paginationChange}
          current={params.pageNum}
          pageSize={params.pageSize}
          total={params.total}
          showSizeChanger={false}
          responsive
          hideOnSinglePage={params.totalPages <= 1}
        />
        <div className={styles['go-to-page']}>
          go to
          <input
            className={styles['page-input']}
            type="text"
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            onKeyDown={handlePageInputKeyDown}
          />
          page
          <Button
            size="small"
            style={{ marginLeft: 8 }}
            onClick={handleGoToPage}
          >
            GO
          </Button>
        </div>
      </div>

      {/* {renderPublishModal()} */}
    </div>
  );
};

export default ServiceCategory; 